import { z } from "zod";

import type {
  KarmaBirthDate,
  KarmaCoachEvidence,
  KarmaCoachGoal,
  KarmaCoachState,
  KarmaCircle,
  KarmaCircleRule,
  KarmaCircleRuleKey,
  KarmaCircleRuleSet,
  KarmaCircleRitual,
  KarmaLike,
  KarmaMatch,
  KarmaMessage,
  KarmaReport,
  KarmaStoreV1,
  KarmaStoreV2,
  KarmaUser,
  KarmaUserIntent,
  KarmaWitnessAction,
  KarmaWitnessRequest,
} from "@/lib/karma/types";

import { computeKarmaNumerology } from "@/lib/karma/numerology";

const STORAGE_KEY_V2 = "karma_store_v2";
const STORAGE_KEY_V1 = "karma_store_v1";

const zBirthDate = z.object({
  day: z.number(),
  month: z.number(),
  year: z.number(),
});

const zPhoto = z.object({
  id: z.string(),
  url: z.string(),
  addedAt: z.string(),
});

const zIntent = z.union([
  z.literal("tanisma"),
  z.literal("arkadaslik"),
  z.literal("ciddi"),
  z.literal("belirsiz"),
]);

const zGender = z.union([
  z.literal("kadin"),
  z.literal("erkek"),
  z.literal("non_binary"),
  z.literal("belirtmek_istemiyorum"),
]);

const zPreferredGender = z.union([
  z.literal("kadin"),
  z.literal("erkek"),
  z.literal("non_binary"),
  z.literal("herkes"),
]);

const zCoachEvidence = z.object({
  text: z.string().optional(),
  checks: z.array(z.string()).optional(),
  photoDataUrl: z.string().optional(),
});

const zCoachState = z.object({
  goal: z
    .union([
      z.literal("iliski"),
      z.literal("para"),
      z.literal("saglik"),
      z.literal("uretim"),
      z.literal("denge"),
    ])
    .optional(),

  completedByDate: z.record(z.array(z.string())).optional(),
  dismissedByDate: z.record(z.array(z.string())).optional(),

  skippedByDate: z.record(z.array(z.string())).optional(),
  evidenceByDate: z.record(z.record(zCoachEvidence)).optional(),
  notesByDate: z.record(z.object({ closingAnswer: z.string().optional() })).optional(),

  submittedByDate: z.record(z.array(z.string())).optional(),
  verifiedByDate: z.record(z.array(z.string())).optional(),
});

const zUser = z.object({
  id: z.string(),
  handle: z.string(),
  displayName: z.string(),
  birthDate: zBirthDate,
  bio: z.string(),
  photos: z.array(zPhoto),
  city: z.string().optional(),
  intent: zIntent.optional(),
  gender: zGender.optional(),
  showBirthDayMonth: z.boolean().optional(),
  matchEnabled: z.boolean(),
  circleId: z.string().optional(),
  reputation: z.number().optional(),
  likes: z.number(),
  likesBase: z.number().optional(),
  blockedUserIds: z.array(z.string()).optional(),
  passedUserIds: z.array(z.string()).optional(),
  prefMinAge: z.number().optional(),
  prefMaxAge: z.number().optional(),
  prefOnlyMatchEnabled: z.boolean().optional(),
  prefDestinyNumbers: z.array(z.number()).optional(),
  prefHeartDesireNumbers: z.array(z.number()).optional(),
  prefExpressionNumbers: z.array(z.number()).optional(),
  prefKarmicDebtNumbers: z.array(z.number()).optional(),
  prefOnlySimilarStatus: z.boolean().optional(),
  prefPartnerGender: zPreferredGender.optional(),
  coach: zCoachState.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const zLike = z.object({
  id: z.string(),
  fromUserId: z.string(),
  toUserId: z.string(),
  createdAt: z.string(),
});

const zMatch = z.object({
  id: z.string(),
  aUserId: z.string(),
  bUserId: z.string(),
  createdAt: z.string(),
  lastMessageAt: z.string().optional(),
});

const zMessage = z.object({
  id: z.string(),
  matchId: z.string(),
  fromUserId: z.string(),
  text: z.string(),
  createdAt: z.string(),
});

const zReport = z.object({
  id: z.string(),
  fromUserId: z.string(),
  targetUserId: z.string(),
  reason: z.string(),
  createdAt: z.string(),
});

const zCircleRuleKey = z.union([
  z.literal("destinyNumber"),
  z.literal("expressionNumber"),
  z.literal("heartDesireNumber"),
  z.literal("personalityNumber"),
  z.literal("birthDayNumber"),
  z.literal("karmicDebt"),
]);

const zCircleRule = z.object({
  key: zCircleRuleKey,
  value: z.number(),
});

const zCircleRuleSet = z.object({
  all: z.array(zCircleRule),
});

const zCircleRitual = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  createdByUserId: z.string(),
  createdAt: z.string(),
});

const zCircle = z.object({
  id: z.string(),
  name: z.string(),
  ownerUserId: z.string().optional(),
  rules: zCircleRuleSet.optional(),
  rituals: z.array(zCircleRitual).optional(),
  memberUserIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const zWitnessStatus = z.union([
  z.literal("pending"),
  z.literal("verified"),
  z.literal("reported"),
]);

const zWitnessRequest = z.object({
  id: z.string(),
  circleId: z.string(),
  fromUserId: z.string(),
  dateKey: z.string(),
  missionId: z.string(),
  requiredCount: z.number(),
  verifierUserIds: z.array(z.string()),
  kudosUserIds: z.array(z.string()),
  reportUserIds: z.array(z.string()),
  status: zWitnessStatus,
  createdAt: z.string(),
  updatedAt: z.string(),
});

const zWitnessAction = z.object({
  id: z.string(),
  circleId: z.string(),
  fromUserId: z.string(),
  targetUserId: z.string(),
  requestId: z.string(),
  kind: z.union([z.literal("verify"), z.literal("kudos"), z.literal("report")]),
  createdAt: z.string(),
});

const zStoreV2 = z.object({
  version: z.literal(2),
  meId: z.string(),
  users: z.array(zUser),
  likes: z.array(zLike),
  matches: z.array(zMatch),
  messages: z.array(zMessage),
  reports: z.array(zReport),
  circles: z.array(zCircle).optional(),
  witnessRequests: z.array(zWitnessRequest).optional(),
  witnessActions: z.array(zWitnessAction).optional(),
});

const zStoreV1 = z.object({
  version: z.literal(1),
  meId: z.string(),
  users: z.array(
    z.object({
      id: z.string(),
      handle: z.string(),
      displayName: z.string(),
      birthDate: zBirthDate,
      bio: z.string(),
      photos: z.array(zPhoto),
      matchEnabled: z.boolean(),
      likes: z.number(),
      createdAt: z.string(),
      updatedAt: z.string(),
    }),
  ),
});

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `id_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function nowIso() {
  return new Date().toISOString();
}

export function toHandle(displayName: string) {
  const base = displayName
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "-")
    .replace(/[çÇ]/g, "c")
    .replace(/[ğĞ]/g, "g")
    .replace(/[ıİ]/g, "i")
    .replace(/[öÖ]/g, "o")
    .replace(/[şŞ]/g, "s")
    .replace(/[üÜ]/g, "u")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return base || `kullanici-${Date.now()}`;
}

function normalizeCoach(value: unknown): KarmaCoachState {
  const empty: KarmaCoachState = {
    goal: undefined,
    completedByDate: {},
    dismissedByDate: {},
    skippedByDate: {},
    evidenceByDate: {},
    notesByDate: {},
    submittedByDate: {},
    verifiedByDate: {},
  };
  if (!value || typeof value !== "object") return empty;

  const v = value as Partial<KarmaCoachState>;

  const normalizeIdMap = (map: unknown) => {
    if (!map || typeof map !== "object") return {};
    const out: Record<string, string[]> = {};
    for (const [k, raw] of Object.entries(map as Record<string, unknown>)) {
      if (!Array.isArray(raw)) continue;
      const arr = raw.filter((x): x is string => typeof x === "string");
      out[k] = arr;
    }
    return out;
  };

  const normalizeEvidenceByDate = (raw: unknown) => {
    if (!raw || typeof raw !== "object") return {};
    const out: Record<string, Record<string, any>> = {};

    for (const [dateKey, dayRaw] of Object.entries(raw as Record<string, unknown>)) {
      if (!dayRaw || typeof dayRaw !== "object") continue;
      const dayOut: Record<string, any> = {};

      for (const [missionId, evRaw] of Object.entries(dayRaw as Record<string, unknown>)) {
        if (!evRaw || typeof evRaw !== "object") continue;
        const ev = evRaw as Record<string, unknown>;
        dayOut[missionId] = {
          text: typeof ev.text === "string" ? ev.text : undefined,
          checks: Array.isArray(ev.checks)
            ? ev.checks.filter((x): x is string => typeof x === "string")
            : undefined,
          photoDataUrl: typeof ev.photoDataUrl === "string" ? ev.photoDataUrl : undefined,
        };
      }

      out[dateKey] = dayOut;
    }

    return out;
  };

  const normalizeNotesByDate = (raw: unknown) => {
    if (!raw || typeof raw !== "object") return {};
    const out: Record<string, { closingAnswer?: string }> = {};

    for (const [dateKey, noteRaw] of Object.entries(raw as Record<string, unknown>)) {
      if (!noteRaw || typeof noteRaw !== "object") continue;
      const note = noteRaw as Record<string, unknown>;
      out[dateKey] = {
        closingAnswer: typeof note.closingAnswer === "string" ? note.closingAnswer : undefined,
      };
    }

    return out;
  };

  const goal =
    v.goal === "iliski" ||
    v.goal === "para" ||
    v.goal === "saglik" ||
    v.goal === "uretim" ||
    v.goal === "denge"
      ? v.goal
      : undefined;

  return {
    goal,
    completedByDate: normalizeIdMap(v.completedByDate),
    dismissedByDate: normalizeIdMap(v.dismissedByDate),
    skippedByDate: normalizeIdMap(v.skippedByDate),
    evidenceByDate: normalizeEvidenceByDate(v.evidenceByDate),
    notesByDate: normalizeNotesByDate(v.notesByDate),
    submittedByDate: normalizeIdMap(v.submittedByDate),
    verifiedByDate: normalizeIdMap(v.verifiedByDate),
  };
}

function normalizeUser(u: KarmaUser) {
  const normalizeDigits = (raw: unknown) => {
    if (!Array.isArray(raw)) return undefined;
    const unique = Array.from(
      new Set(
        raw
          .map((x) => Math.trunc(Number(x)))
          .filter((n) => Number.isFinite(n) && n >= 1 && n <= 9),
      ),
    ) as number[];
    return unique.length ? unique : undefined;
  };

  const normalizeGender = (raw: unknown) => {
    if (raw === "kadin" || raw === "erkek" || raw === "non_binary" || raw === "belirtmek_istemiyorum") return raw;
    return undefined;
  };

  const normalizePreferredGender = (raw: unknown) => {
    if (raw === "kadin" || raw === "erkek" || raw === "non_binary" || raw === "herkes") return raw;
    return undefined;
  };

  return {
    ...u,
    bio: u.bio ?? "",
    photos: Array.isArray(u.photos) ? u.photos : [],
    city: u.city?.trim() || undefined,
    intent: u.intent,
    gender: normalizeGender(u.gender),
    showBirthDayMonth: u.showBirthDayMonth ?? false,
    coach: normalizeCoach(u.coach),
    circleId: typeof u.circleId === "string" ? u.circleId : undefined,
    reputation: typeof u.reputation === "number" ? u.reputation : 0,
    likesBase: typeof u.likesBase === "number" ? u.likesBase : (u.likes ?? 0),
    likes: typeof u.likes === "number" ? u.likes : 0,
    blockedUserIds: Array.isArray(u.blockedUserIds) ? u.blockedUserIds : [],
    passedUserIds: Array.isArray(u.passedUserIds) ? u.passedUserIds : [],
    prefMinAge: typeof u.prefMinAge === "number" ? u.prefMinAge : undefined,
    prefMaxAge: typeof u.prefMaxAge === "number" ? u.prefMaxAge : undefined,
    prefOnlyMatchEnabled:
      typeof u.prefOnlyMatchEnabled === "boolean" ? u.prefOnlyMatchEnabled : undefined,
    prefDestinyNumbers: normalizeDigits(u.prefDestinyNumbers),
    prefHeartDesireNumbers: normalizeDigits(u.prefHeartDesireNumbers),
    prefExpressionNumbers: normalizeDigits(u.prefExpressionNumbers),
    prefKarmicDebtNumbers: normalizeDigits(u.prefKarmicDebtNumbers),
    prefOnlySimilarStatus:
      typeof u.prefOnlySimilarStatus === "boolean" ? u.prefOnlySimilarStatus : undefined,
    prefPartnerGender: normalizePreferredGender(u.prefPartnerGender),
    matchEnabled: Boolean(u.matchEnabled),
  } satisfies KarmaUser;
}

function normalizeRuleKey(raw: unknown): KarmaCircleRuleKey | null {
  if (
    raw === "destinyNumber" ||
    raw === "expressionNumber" ||
    raw === "heartDesireNumber" ||
    raw === "personalityNumber" ||
    raw === "birthDayNumber" ||
    raw === "karmicDebt"
  ) {
    return raw;
  }
  return null;
}

function normalizeRule(raw: unknown): KarmaCircleRule | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const key = normalizeRuleKey(r.key);
  const value = typeof r.value === "number" ? Math.trunc(r.value) : Math.trunc(Number(r.value));
  if (!key || !Number.isFinite(value)) return null;
  return { key, value };
}

function normalizeRuleSet(raw: unknown): KarmaCircleRuleSet {
  if (!raw || typeof raw !== "object") return { all: [] };
  const rs = raw as Record<string, unknown>;
  const rules = Array.isArray(rs.all) ? rs.all.map(normalizeRule).filter(Boolean) : [];
  return { all: rules as KarmaCircleRule[] };
}

function normalizeRitual(raw: unknown): KarmaCircleRitual | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === "string" ? r.id : null;
  const title = typeof r.title === "string" ? r.title : null;
  const description = typeof r.description === "string" ? r.description : null;
  const createdByUserId = typeof r.createdByUserId === "string" ? r.createdByUserId : null;
  const createdAt = typeof r.createdAt === "string" ? r.createdAt : null;
  if (!id || !title || !description || !createdByUserId || !createdAt) return null;
  return { id, title, description, createdByUserId, createdAt };
}

function uniq(arr: string[]) {
  return Array.from(new Set(arr.filter((x) => typeof x === "string" && x.trim().length)));
}

function normalizeCircle(c: KarmaCircle, meId: string): KarmaCircle {
  const memberUserIds = uniq(Array.isArray(c.memberUserIds) ? c.memberUserIds : []);
  const ownerUserId = typeof c.ownerUserId === "string" ? c.ownerUserId : memberUserIds[0] ?? meId;
  const rules = normalizeRuleSet(c.rules);
  const rituals = Array.isArray(c.rituals) ? c.rituals.map(normalizeRitual).filter(Boolean) : [];

  return {
    ...c,
    name: String(c.name ?? "").trim() || "Çember",
    ownerUserId,
    rules,
    rituals: rituals as KarmaCircleRitual[],
    memberUserIds,
  } satisfies KarmaCircle;
}

function computeLikesCount(userId: string, store: KarmaStoreV2) {
  const base = store.users.find((u) => u.id === userId)?.likesBase ?? 0;
  const received = store.likes.filter((l) => l.toUserId === userId).length;
  return base + received;
}

function hydrateCounts(store: KarmaStoreV2): KarmaStoreV2 {
  const users = store.users.map((u) => {
    const likes = computeLikesCount(u.id, store);
    return { ...u, likes };
  });
  return { ...store, users };
}

function buildCirclesFromUsers(users: KarmaUser[]): { circles: KarmaCircle[]; users: KarmaUser[] } {
  const createdAt = nowIso();
  const size = 8;
  const chunks: KarmaUser[][] = [];

  for (let i = 0; i < users.length; i += size) {
    chunks.push(users.slice(i, i + size));
  }

  const circles: KarmaCircle[] = chunks.map((chunk, idx) => {
    const id = `circle_${idx + 1}`;
    const memberUserIds = chunk.map((u) => u.id);
    return {
      id,
      name: `Çember ${idx + 1}`,
      ownerUserId: memberUserIds[0],
      rules: { all: [] },
      rituals: [],
      memberUserIds,
      createdAt,
      updatedAt: createdAt,
    };
  });

  const userCircleMap = new Map<string, string>();
  for (const c of circles) {
    for (const userId of c.memberUserIds) {
      if (!userCircleMap.has(userId)) userCircleMap.set(userId, c.id);
    }
  }

  const nextUsers = users.map((u) => ({ ...u, circleId: userCircleMap.get(u.id) }));
  return { circles, users: nextUsers };
}

function normalizeStore(store: KarmaStoreV2): KarmaStoreV2 {
  const users = store.users.map(normalizeUser);
  const withUsers = { ...store, users };

  const circles = Array.isArray(withUsers.circles)
    ? withUsers.circles.map((c) => normalizeCircle(c, withUsers.meId))
    : [];
  const witnessRequests = Array.isArray(withUsers.witnessRequests) ? withUsers.witnessRequests : [];
  const witnessActions = Array.isArray(withUsers.witnessActions) ? withUsers.witnessActions : [];

  let next: KarmaStoreV2 = { ...withUsers, circles, witnessRequests, witnessActions };

  if (next.circles.length === 0) {
    const seeded = buildCirclesFromUsers(next.users);
    next = { ...next, users: seeded.users, circles: seeded.circles };
  } else {
    const circleIds = new Set(next.circles.map((c) => c.id));
    const usersWithCircle = next.users.map((u) => {
      if (u.circleId && circleIds.has(u.circleId)) return u;
      const found = next.circles.find((c) => c.memberUserIds.includes(u.id));
      return { ...u, circleId: found?.id };
    });
    next = { ...next, users: usersWithCircle };
  }

  return hydrateCounts(next);
}

function migrateV1toV2(v1: KarmaStoreV1): KarmaStoreV2 {
  const users: KarmaUser[] = v1.users.map((u) =>
    normalizeUser({
      ...u,
      likesBase: u.likes ?? 0,
      likes: 0,
      blockedUserIds: [],
      passedUserIds: [],
      prefMinAge: undefined,
      prefMaxAge: undefined,
      prefOnlyMatchEnabled: undefined,
      prefDestinyNumbers: undefined,
      prefHeartDesireNumbers: undefined,
      prefExpressionNumbers: undefined,
      prefKarmicDebtNumbers: undefined,
      prefOnlySimilarStatus: undefined,
      prefPartnerGender: undefined,
      showBirthDayMonth: false,
      intent: undefined,
      gender: undefined,
      city: undefined,
      reputation: 0,
      circleId: undefined,
    }),
  );

  const seeded = buildCirclesFromUsers(users);

  const v2: KarmaStoreV2 = {
    version: 2,
    meId: v1.meId,
    users: seeded.users,
    likes: [],
    matches: [],
    messages: [],
    reports: [],
    circles: seeded.circles,
    witnessRequests: [],
    witnessActions: [],
  };

  return normalizeStore(v2);
}

function seedUsers(): { me: KarmaUser; others: KarmaUser[] } {
  const createdAt = nowIso();
  const me: KarmaUser = {
    id: uuid(),
    handle: `user_${Math.random().toString(36).slice(2, 10)}`,
    displayName: "Kullanıcı",
    birthDate: { day: 1, month: 1, year: 2000 },
    bio: "",
    photos: [],
    city: "",
    intent: "tanisma",
    gender: undefined,
    showBirthDayMonth: false,
    matchEnabled: true,
    coach: {
      goal: "denge",
      completedByDate: {},
      dismissedByDate: {},
      skippedByDate: {},
      evidenceByDate: {},
      notesByDate: {},
      submittedByDate: {},
      verifiedByDate: {},
    },
    likes: 0,
    likesBase: 0,
    blockedUserIds: [],
    passedUserIds: [],
    prefMinAge: 18,
    prefMaxAge: 99,
    prefOnlyMatchEnabled: false,
    prefOnlySimilarStatus: false,
    prefPartnerGender: "herkes",
    createdAt,
    updatedAt: createdAt,
  };

  const others: KarmaUser[] = [];

  return { me, others };
}

export function loadStore(): KarmaStoreV2 | null {
  if (typeof window === "undefined") return null;

  const rawV2 = window.localStorage.getItem(STORAGE_KEY_V2);
  if (rawV2) {
    try {
      const parsed = JSON.parse(rawV2);
      const result = zStoreV2.safeParse(parsed);
      if (result.success) return normalizeStore(result.data as KarmaStoreV2);
    } catch {
      // ignore
    }
  }

  const rawV1 = window.localStorage.getItem(STORAGE_KEY_V1);
  if (rawV1) {
    try {
      const parsed = JSON.parse(rawV1);
      const result = zStoreV1.safeParse(parsed);
      if (result.success) {
        const migrated = migrateV1toV2(result.data as KarmaStoreV1);
        saveStore(migrated);
        return migrated;
      }
    } catch {
      // ignore
    }
  }

  return null;
}

export function saveStore(store: KarmaStoreV2) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY_V2, JSON.stringify(store));
}

export function resetMatchesOnly() {
  if (typeof window === "undefined") return;
  try {
    const store = loadStore();
    if (!store) return;
    // Keep messages but reset matches so user starts fresh
    store.matches = [];
    store.likes = [];
    saveStore(store);
  } catch {
    // ignore
  }
}

export function clearStore() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY_V2);
    window.localStorage.removeItem(STORAGE_KEY_V1);
  } catch {
    // ignore
  }
}

function sanitizeLegacySeedDefaults(store: KarmaStoreV2): KarmaStoreV2 {
  const me = store.users.find((u) => u.id === store.meId);
  if (!me) return store;

  const isLegacySeedName = me.displayName.trim().toLocaleLowerCase("tr-TR") === "bir fincan sevgi";
  const isLegacySeedHandle = me.handle.trim().toLocaleLowerCase("tr-TR") === "bir-fincan-sevgi";
  const isLegacySeedBirthDate = me.birthDate.day === 3 && me.birthDate.month === 3 && me.birthDate.year === 1993;
  const hasLegacySeedBio = me.bio.includes("Numerolojiyle kendini tanı");

  if (!isLegacySeedName && !isLegacySeedHandle && !isLegacySeedBirthDate && !hasLegacySeedBio) return store;

  const sanitizedMe = normalizeUser({
    ...me,
    handle: isLegacySeedHandle ? `user_${me.id.slice(0, 8)}` : me.handle,
    displayName: isLegacySeedName ? "Kullanıcı" : me.displayName,
    birthDate: isLegacySeedBirthDate ? { day: 1, month: 1, year: 2000 } : me.birthDate,
    bio: hasLegacySeedBio ? "" : me.bio,
    updatedAt: nowIso(),
  });

  const users = store.users.map((u) => (u.id === sanitizedMe.id ? sanitizedMe : u));
  return normalizeStore({ ...store, users });
}

export function ensureStore(): KarmaStoreV2 {
  const existing = loadStore();
  if (existing) {
    const sanitized = sanitizeLegacySeedDefaults(existing);
    if (sanitized !== existing) saveStore(sanitized);
    return sanitized;
  }

  const { me, others } = seedUsers();
  const users = [me, ...others].map(normalizeUser);
  const seeded = buildCirclesFromUsers(users);

  const store: KarmaStoreV2 = {
    version: 2,
    meId: me.id,
    users: seeded.users,
    likes: [],
    matches: [],
    messages: [],
    reports: [],
    circles: seeded.circles,
    witnessRequests: [],
    witnessActions: [],
  };

  const normalized = normalizeStore(store);
  const sanitized = sanitizeLegacySeedDefaults(normalized);
  saveStore(sanitized);
  return sanitized;
}

export function getMe(store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.users.find((u) => u.id === s.meId) ?? s.users[0];
}

export function listUsers(store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.users;
}

export function getUserById(userId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.users.find((u) => u.id === userId) ?? null;
}

export function getUserByHandle(handle: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.users.find((u) => u.handle === handle) ?? null;
}

export function updateUser(updated: KarmaUser) {
  const store = ensureStore();
  const next: KarmaUser = normalizeUser({
    ...updated,
    updatedAt: nowIso(),
  });

  const users = store.users.map((u) => (u.id === next.id ? next : u));
  const nextStore: KarmaStoreV2 = hydrateCounts({ ...store, users });
  saveStore(nextStore);
  return nextStore;
}

export function toggleCoachMissionCompleted(missionId: string, dateKey: string) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const current = coach.completedByDate[dateKey] ?? [];
  const set = new Set(current);

  if (set.has(missionId)) set.delete(missionId);
  else set.add(missionId);

  const nextCoach: KarmaCoachState = {
    ...coach,
    completedByDate: { ...coach.completedByDate, [dateKey]: Array.from(set) },
  };

  return updateUser({ ...me, coach: nextCoach });
}

export function dismissCoachMission(missionId: string, dateKey: string) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const current = coach.dismissedByDate[dateKey] ?? [];
  const set = new Set(current);
  set.add(missionId);

  const nextCoach: KarmaCoachState = {
    ...coach,
    dismissedByDate: { ...coach.dismissedByDate, [dateKey]: Array.from(set) },
  };

  return updateUser({ ...me, coach: nextCoach });
}

export function skipCoachMission(missionId: string, dateKey: string) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const today = coach.skippedByDate[dateKey] ?? [];

  if (today.includes(missionId)) return store;
  if (today.length >= 1) return store;

  const nextCoach: KarmaCoachState = {
    ...coach,
    skippedByDate: { ...coach.skippedByDate, [dateKey]: [...today, missionId] },
  };

  return updateUser({ ...me, coach: nextCoach });
}

export function setCoachGoal(goal: KarmaCoachGoal) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const nextCoach: KarmaCoachState = { ...coach, goal };
  return updateUser({ ...me, coach: nextCoach });
}

export function setCoachEvidence(dateKey: string, missionId: string, evidence: KarmaCoachEvidence) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const day = coach.evidenceByDate[dateKey] ?? {};
  const current = (day[missionId] ?? {}) as KarmaCoachEvidence;

  const nextCoach: KarmaCoachState = {
    ...coach,
    evidenceByDate: {
      ...coach.evidenceByDate,
      [dateKey]: {
        ...day,
        [missionId]: { ...current, ...evidence },
      },
    },
  };

  return updateUser({ ...me, coach: nextCoach });
}

export function setCoachClosingAnswer(dateKey: string, closingAnswer: string) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const nextCoach: KarmaCoachState = {
    ...coach,
    notesByDate: {
      ...coach.notesByDate,
      [dateKey]: { ...(coach.notesByDate[dateKey] ?? {}), closingAnswer },
    },
  };

  return updateUser({ ...me, coach: nextCoach });
}

export function resetCoachDay(dateKey: string) {
  const store = ensureStore();
  const me = getMe(store);

  const coach = normalizeCoach(me.coach);
  const { [dateKey]: _completed, ...completedRest } = coach.completedByDate;
  const { [dateKey]: _dismissed, ...dismissedRest } = coach.dismissedByDate;
  const { [dateKey]: _skipped, ...skippedRest } = coach.skippedByDate;
  const { [dateKey]: _evidence, ...evidenceRest } = coach.evidenceByDate;
  const { [dateKey]: _note, ...notesRest } = coach.notesByDate;
  const { [dateKey]: _submitted, ...submittedRest } = coach.submittedByDate;
  const { [dateKey]: _verified, ...verifiedRest } = coach.verifiedByDate;

  const nextCoach: KarmaCoachState = {
    ...coach,
    completedByDate: completedRest,
    dismissedByDate: dismissedRest,
    skippedByDate: skippedRest,
    evidenceByDate: evidenceRest,
    notesByDate: notesRest,
    submittedByDate: submittedRest,
    verifiedByDate: verifiedRest,
  };

  return updateUser({ ...me, coach: nextCoach });
}

function isoDateKey(iso: string) {
  return iso.slice(0, 10);
}

export function listCircles(store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.circles.slice().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getCircleById(circleId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.circles.find((c) => c.id === circleId) ?? null;
}

function userMatchesCircleRules(user: KarmaUser, circle: KarmaCircle) {
  const rules = circle.rules?.all ?? [];
  if (rules.length === 0) return true;

  const nu = computeKarmaNumerology(user.displayName, user.birthDate);

  const karmicDebtExpected = rules
    .filter((r) => r.key === "karmicDebt")
    .map((r) => Math.trunc(r.value))
    .filter((n) => Number.isFinite(n));

  if (karmicDebtExpected.length) {
    const ok = karmicDebtExpected.some((n) => nu.karmicCodes.includes(n));
    if (!ok) return false;
  }

  for (const rule of rules) {
    if (rule.key === "karmicDebt") continue;

    const expected = Math.trunc(rule.value);

    const actual =
      rule.key === "destinyNumber"
        ? nu.destinyNumber
        : rule.key === "expressionNumber"
          ? nu.expressionNumber
          : rule.key === "heartDesireNumber"
            ? nu.heartDesireNumber
            : rule.key === "personalityNumber"
              ? nu.personalityNumber
              : nu.birthDayNumber;

    if (actual !== expected) return false;
  }

  return true;
}

export function listEligibleCirclesForUser(userId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  const user = s.users.find((u) => u.id === userId);
  if (!user) return [];

  return listCircles(s).filter((c) => userMatchesCircleRules(user, c));
}

export function addCircleRitual(circleId: string, title: string, description: string) {
  const store = ensureStore();
  const me = getMe(store);

  const circle = store.circles.find((c) => c.id === circleId);
  if (!circle) return store;
  if (circle.ownerUserId !== me.id) return store;

  const t = title.trim().slice(0, 60);
  const d = description.trim().slice(0, 280);
  if (t.length < 2 || d.length < 2) return store;

  const createdAt = nowIso();
  const ritual: KarmaCircleRitual = {
    id: uuid(),
    title: t,
    description: d,
    createdByUserId: me.id,
    createdAt,
  };

  const updatedCircle: KarmaCircle = {
    ...circle,
    rituals: [ritual, ...((circle.rituals ?? []) as KarmaCircleRitual[])].slice(0, 50),
    updatedAt: createdAt,
  };

  const circles = store.circles.map((c) => (c.id === circleId ? updatedCircle : c));
  return saveNormalizedStore({ ...store, circles });
}

function removeUserFromCircles(store: KarmaStoreV2, userId: string) {
  const updatedAt = nowIso();
  let changed = false;

  const circles = store.circles.map((c) => {
    if (!c.memberUserIds.includes(userId)) return c;
    changed = true;
    return {
      ...c,
      memberUserIds: c.memberUserIds.filter((id) => id !== userId),
      updatedAt,
    };
  });

  return { circles, changed };
}

function saveNormalizedStore(next: KarmaStoreV2) {
  const normalized = normalizeStore(next);
  saveStore(normalized);
  return normalized;
}

export function leaveMyCircle() {
  const store = ensureStore();
  const me = getMe(store);

  if (!me.circleId) return store;

  const { circles } = removeUserFromCircles(store, me.id);

  const users = store.users.map((u) => (u.id === me.id ? ({ ...u, circleId: undefined } satisfies KarmaUser) : u));

  return saveNormalizedStore({ ...store, users, circles });
}

export function joinCircle(circleId: string) {
  const store = ensureStore();
  const me = getMe(store);

  const target = store.circles.find((c) => c.id === circleId);
  if (!target) return store;

  if (!userMatchesCircleRules(me, target)) return store;

  if (target.memberUserIds.includes(me.id) && me.circleId === target.id) return store;
  if (target.memberUserIds.length >= 12) return store;

  const updatedAt = nowIso();
  const { circles: removedCircles } = removeUserFromCircles(store, me.id);

  const circles = removedCircles.map((c) => {
    if (c.id !== target.id) return c;
    const nextMembers = Array.from(new Set([...c.memberUserIds, me.id]));
    return { ...c, memberUserIds: nextMembers, updatedAt };
  });

  const users = store.users.map((u) => (u.id === me.id ? ({ ...u, circleId: target.id } satisfies KarmaUser) : u));

  return saveNormalizedStore({ ...store, users, circles });
}

export function createCircle(args: { name: string; rules?: KarmaCircleRuleSet }) {
  const store = ensureStore();
  const me = getMe(store);

  const cleanName = args.name.trim().slice(0, 40);
  if (cleanName.length < 2) return store;

  const createdAt = nowIso();
  const { circles: removedCircles } = removeUserFromCircles(store, me.id);

  const rules = normalizeRuleSet(args.rules);

  const newCircle: KarmaCircle = {
    id: `circle_${uuid()}`,
    name: cleanName,
    ownerUserId: me.id,
    rules,
    rituals: [],
    memberUserIds: [me.id],
    createdAt,
    updatedAt: createdAt,
  };

  const users = store.users.map((u) => (u.id === me.id ? ({ ...u, circleId: newCircle.id } satisfies KarmaUser) : u));

  return saveNormalizedStore({ ...store, users, circles: [newCircle, ...removedCircles] });
}

export function getMyCircle(store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  const me = getMe(s);
  if (!me.circleId) return null;
  return s.circles.find((c) => c.id === me.circleId) ?? null;
}

export function listCircleMembers(circleId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  const circle = s.circles.find((c) => c.id === circleId);
  if (!circle) return [];
  const users = circle.memberUserIds
    .map((id) => s.users.find((u) => u.id === id) ?? null)
    .filter((u): u is KarmaUser => Boolean(u));
  return users;
}

export function getWitnessRequestForMission(args: {
  fromUserId: string;
  dateKey: string;
  missionId: string;
  store?: KarmaStoreV2;
}) {
  const s = args.store ?? ensureStore();
  return (
    s.witnessRequests.find(
      (r) =>
        r.fromUserId === args.fromUserId &&
        r.dateKey === args.dateKey &&
        r.missionId === args.missionId &&
        r.status !== "reported",
    ) ?? null
  );
}

function hasProofForMission(user: KarmaUser, dateKey: string, missionId: string) {
  const coach = normalizeCoach(user.coach);
  const evidence = coach.evidenceByDate[dateKey]?.[missionId] ?? {};

  if (missionId === "core-starter") {
    return (evidence.checks ?? []).includes("__timer_done");
  }
  if (missionId === "core-main") {
    const answer = (evidence.checks ?? [])[0] ?? "";
    return Boolean(answer);
  }
  if (missionId === "core-closing") {
    const text = coach.notesByDate[dateKey]?.closingAnswer ?? "";
    return text.trim().length > 0;
  }
  if (missionId === "bonus-affirmation") {
    return (evidence.checks ?? []).includes("__timer_done");
  }

  return true;
}

export function submitWitnessRequest(dateKey: string, missionId: string) {
  const store = ensureStore();
  const me = getMe(store);
  if (!me.circleId) return store;

  const coach = normalizeCoach(me.coach);
  const done = (coach.completedByDate[dateKey] ?? []).includes(missionId);
  if (!done) return store;
  if (!hasProofForMission(me, dateKey, missionId)) return store;

  const existing = getWitnessRequestForMission({ fromUserId: me.id, dateKey, missionId, store });
  if (existing) return store;

  const createdAt = nowIso();
  const req: KarmaWitnessRequest = {
    id: uuid(),
    circleId: me.circleId,
    fromUserId: me.id,
    dateKey,
    missionId,
    requiredCount: 2,
    verifierUserIds: [],
    kudosUserIds: [],
    reportUserIds: [],
    status: "pending",
    createdAt,
    updatedAt: createdAt,
  };

  const nextCoach: KarmaCoachState = {
    ...coach,
    submittedByDate: {
      ...coach.submittedByDate,
      [dateKey]: Array.from(new Set([...(coach.submittedByDate[dateKey] ?? []), missionId])),
    },
  };

  const nextUsers = store.users.map((u) =>
    u.id === me.id ? ({ ...me, coach: nextCoach } satisfies KarmaUser) : u,
  );

  const next: KarmaStoreV2 = {
    ...store,
    users: nextUsers,
    witnessRequests: [req, ...store.witnessRequests],
  };

  saveStore(next);
  return hydrateCounts(next);
}

export function listPendingWitnessQueue(circleId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.witnessRequests
    .filter((r) => r.circleId === circleId)
    .filter((r) => r.status === "pending")
    .slice()
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function canGiveVerify(meId: string, targetUserId: string, store: KarmaStoreV2) {
  const today = isoDateKey(nowIso());
  const myVerifiesToday = store.witnessActions.filter(
    (a) => a.kind === "verify" && a.fromUserId === meId && isoDateKey(a.createdAt) === today,
  );
  if (myVerifiesToday.length >= 10) return false;

  const alreadyToTargetToday = myVerifiesToday.some((a) => a.targetUserId === targetUserId);
  if (alreadyToTargetToday) return false;

  return true;
}

export function verifyWitnessRequest(requestId: string) {
  const store = ensureStore();
  const me = getMe(store);

  const reqIdx = store.witnessRequests.findIndex((r) => r.id === requestId);
  if (reqIdx < 0) return store;
  const req = store.witnessRequests[reqIdx];

  if (req.status !== "pending") return store;
  if (req.fromUserId === me.id) return store;
  if (!me.circleId || me.circleId !== req.circleId) return store;
  if (req.verifierUserIds.includes(me.id)) return store;

  if (!canGiveVerify(me.id, req.fromUserId, store)) return store;

  const now = nowIso();
  const nextVerifierUserIds = [...req.verifierUserIds, me.id];

  let status: KarmaWitnessRequest["status"] = req.status;
  if (nextVerifierUserIds.length >= req.requiredCount) status = "verified";

  const updatedReq: KarmaWitnessRequest = {
    ...req,
    verifierUserIds: nextVerifierUserIds,
    status,
    updatedAt: now,
  };

  const action: KarmaWitnessAction = {
    id: uuid(),
    circleId: req.circleId,
    fromUserId: me.id,
    targetUserId: req.fromUserId,
    requestId: req.id,
    kind: "verify",
    createdAt: now,
  };

  const users = store.users.map((u) => {
    if (u.id === me.id) {
      return { ...u, reputation: (u.reputation ?? 0) + 1 };
    }

    if (status === "verified" && u.id === req.fromUserId) {
      const coach = normalizeCoach(u.coach);
      const current = coach.verifiedByDate[req.dateKey] ?? [];
      const nextCoach: KarmaCoachState = {
        ...coach,
        verifiedByDate: {
          ...coach.verifiedByDate,
          [req.dateKey]: Array.from(new Set([...current, req.missionId])),
        },
      };

      return {
        ...u,
        reputation: (u.reputation ?? 0) + 2,
        coach: nextCoach,
      };
    }

    return u;
  });

  const witnessRequests = store.witnessRequests.slice();
  witnessRequests[reqIdx] = updatedReq;

  const next: KarmaStoreV2 = {
    ...store,
    users,
    witnessRequests,
    witnessActions: [action, ...store.witnessActions],
  };

  saveStore(next);
  return hydrateCounts(next);
}

export function giveKudosToWitnessRequest(requestId: string) {
  const store = ensureStore();
  const me = getMe(store);

  const reqIdx = store.witnessRequests.findIndex((r) => r.id === requestId);
  if (reqIdx < 0) return store;
  const req = store.witnessRequests[reqIdx];

  if (!me.circleId || me.circleId !== req.circleId) return store;
  if (req.fromUserId === me.id) return store;
  if (req.kudosUserIds.includes(me.id)) return store;

  const now = nowIso();

  const updatedReq: KarmaWitnessRequest = {
    ...req,
    kudosUserIds: [...req.kudosUserIds, me.id],
    updatedAt: now,
  };

  const action: KarmaWitnessAction = {
    id: uuid(),
    circleId: req.circleId,
    fromUserId: me.id,
    targetUserId: req.fromUserId,
    requestId: req.id,
    kind: "kudos",
    createdAt: now,
  };

  const users = store.users.map((u) => {
    if (u.id === req.fromUserId) return { ...u, reputation: (u.reputation ?? 0) + 1 };
    return u;
  });

  const witnessRequests = store.witnessRequests.slice();
  witnessRequests[reqIdx] = updatedReq;

  const next: KarmaStoreV2 = {
    ...store,
    users,
    witnessRequests,
    witnessActions: [action, ...store.witnessActions],
  };

  saveStore(next);
  return hydrateCounts(next);
}

export function reportWitnessRequest(requestId: string) {
  const store = ensureStore();
  const me = getMe(store);

  const reqIdx = store.witnessRequests.findIndex((r) => r.id === requestId);
  if (reqIdx < 0) return store;
  const req = store.witnessRequests[reqIdx];

  if (!me.circleId || me.circleId !== req.circleId) return store;
  if (req.reportUserIds.includes(me.id)) return store;

  const now = nowIso();
  const reportUserIds = [...req.reportUserIds, me.id];
  const status: KarmaWitnessRequest["status"] = reportUserIds.length >= 2 ? "reported" : req.status;

  const updatedReq: KarmaWitnessRequest = {
    ...req,
    reportUserIds,
    status,
    updatedAt: now,
  };

  const action: KarmaWitnessAction = {
    id: uuid(),
    circleId: req.circleId,
    fromUserId: me.id,
    targetUserId: req.fromUserId,
    requestId: req.id,
    kind: "report",
    createdAt: now,
  };

  const witnessRequests = store.witnessRequests.slice();
  witnessRequests[reqIdx] = updatedReq;

  const next: KarmaStoreV2 = {
    ...store,
    witnessRequests,
    witnessActions: [action, ...store.witnessActions],
  };

  saveStore(next);
  return hydrateCounts(next);
}

export function upsertUser(partial: Partial<KarmaUser> & Pick<KarmaUser, "id">) {
  const store = ensureStore();
  const current = store.users.find((u) => u.id === partial.id);
  if (!current) return store;
  return updateUser({ ...current, ...partial });
}

export function addPhoto(userId: string, url: string) {
  const store = ensureStore();
  const user = store.users.find((u) => u.id === userId);
  if (!user) return store;

  const photo = { id: uuid(), url, addedAt: nowIso() };
  return updateUser({ ...user, photos: [photo, ...user.photos] });
}

function matchKey(a: string, b: string) {
  const [x, y] = [a, b].sort();
  return `${x}__${y}`;
}

function hasMatch(store: KarmaStoreV2, aUserId: string, bUserId: string) {
  const key = matchKey(aUserId, bUserId);
  return store.matches.some((m) => matchKey(m.aUserId, m.bUserId) === key);
}

function createMatch(store: KarmaStoreV2, aUserId: string, bUserId: string) {
  if (hasMatch(store, aUserId, bUserId)) return store;
  const createdAt = nowIso();
  const match: KarmaMatch = {
    id: uuid(),
    aUserId,
    bUserId,
    createdAt,
    lastMessageAt: createdAt,
  };

  return { ...store, matches: [match, ...store.matches] };
}

export function ensurePrivateMatchForUser(args: {
  peerId: string;
  peerDisplayName: string;
  peerHandle?: string;
  peerPhotoUrl?: string | null;
  peerBirthDate?: KarmaBirthDate;
}) {
  const store = ensureStore();
  const me = getMe(store);

  if (!args.peerId || args.peerId === me.id) return { matchId: null as string | null, store };

  let next = store;
  const existingPeer = next.users.find((u) => u.id === args.peerId);

  if (!existingPeer) {
    const now = nowIso();
    const handleBase = args.peerHandle?.trim() || toHandle(args.peerDisplayName || "kullanici");
    const peer: KarmaUser = {
      id: args.peerId,
      handle: handleBase || `user-${args.peerId.slice(0, 8)}`,
      displayName: args.peerDisplayName?.trim() || "Kullanıcı",
      birthDate: args.peerBirthDate ?? me.birthDate,
      bio: "",
      photos: args.peerPhotoUrl
        ? [
            {
              id: uuid(),
              url: args.peerPhotoUrl,
              addedAt: now,
            },
          ]
        : [],
      matchEnabled: true,
      likes: 0,
      createdAt: now,
      updatedAt: now,
    };

    next = { ...next, users: [peer, ...next.users] };
  }

  next = createMatch(next, me.id, args.peerId);
  next = hydrateCounts(next);
  saveStore(next);

  const match = next.matches.find((m) => matchKey(m.aUserId, m.bUserId) === matchKey(me.id, args.peerId)) ?? null;
  return { matchId: match?.id ?? null, store: next };
}

export function toggleLike(targetUserId: string) {
  const store = ensureStore();
  const me = getMe(store);
  if (me.id === targetUserId) return store;

  if (me.blockedUserIds?.includes(targetUserId)) return store;
  if (me.passedUserIds?.includes(targetUserId)) return store;
  const target = store.users.find((u) => u.id === targetUserId);
  if (!target) return store;
  if (target.blockedUserIds?.includes(me.id)) return store;

  const existing = store.likes.find(
    (l) => l.fromUserId === me.id && l.toUserId === targetUserId,
  );

  let next: KarmaStoreV2;

  if (existing) {
    next = { ...store, likes: store.likes.filter((l) => l.id !== existing.id) };
  } else {
    const like: KarmaLike = {
      id: uuid(),
      fromUserId: me.id,
      toUserId: targetUserId,
      createdAt: nowIso(),
    };
    next = { ...store, likes: [like, ...store.likes] };

    const reciprocal = next.likes.some(
      (l) => l.fromUserId === targetUserId && l.toUserId === me.id,
    );
    if (reciprocal) {
      next = createMatch(next, me.id, targetUserId);
    }
  }

  next = hydrateCounts(next);
  saveStore(next);
  return next;
}

export function listLikesGiven(userId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.likes.filter((l) => l.fromUserId === userId);
}

export function listLikesReceived(userId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.likes.filter((l) => l.toUserId === userId);
}

export function listMatchesForUser(userId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.matches
    .filter((m) => m.aUserId === userId || m.bUserId === userId)
    .slice()
    .sort((a, b) => {
      const at = a.lastMessageAt ?? a.createdAt;
      const bt = b.lastMessageAt ?? b.createdAt;
      return bt.localeCompare(at);
    });
}

export function getMatchById(matchId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.matches.find((m) => m.id === matchId) ?? null;
}

export function getOtherUserIdFromMatch(match: KarmaMatch, meId: string) {
  return match.aUserId === meId ? match.bUserId : match.aUserId;
}

export function listMessages(matchId: string, store?: KarmaStoreV2) {
  const s = store ?? ensureStore();
  return s.messages
    .filter((m) => m.matchId === matchId)
    .slice()
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function appendMessageToMatch(args: {
  matchId: string;
  fromUserId: string;
  text: string;
  messageId?: string;
  createdAt?: string;
}) {
  const store = ensureStore();
  const match = store.matches.find((m) => m.id === args.matchId);
  if (!match) return store;
  if (match.aUserId !== args.fromUserId && match.bUserId !== args.fromUserId) return store;

  const trimmed = args.text.trim();
  if (!trimmed) return store;

  if (args.messageId && store.messages.some((m) => m.id === args.messageId)) {
    return store;
  }

  const createdAt = args.createdAt ?? nowIso();
  const msg: KarmaMessage = {
    id: args.messageId ?? uuid(),
    matchId: args.matchId,
    fromUserId: args.fromUserId,
    text: trimmed,
    createdAt,
  };

  const messages = [...store.messages, msg];
  const matches = store.matches.map((m) => {
    if (m.id !== args.matchId) return m;
    const last = m.lastMessageAt ?? m.createdAt;
    return createdAt > last ? { ...m, lastMessageAt: createdAt } : m;
  });

  const next = hydrateCounts({ ...store, messages, matches });
  saveStore(next);
  return next;
}

export function sendMessage(matchId: string, text: string, fromUserId?: string) {
  const store = ensureStore();
  const me = getMe(store);
  const senderId = typeof fromUserId === "string" && fromUserId.trim() ? fromUserId.trim() : me.id;
  return appendMessageToMatch({
    matchId,
    fromUserId: senderId,
    text,
  });
}

export function deleteMessageById(messageId: string) {
  const store = ensureStore();
  const id = String(messageId ?? "").trim();
  if (!id) return store;

  const target = store.messages.find((message) => message.id === id);
  if (!target) return store;

  const messages = store.messages.filter((message) => message.id !== id);
  const affectedMatchId = target.matchId;

  const matches = store.matches.map((match) => {
    if (match.id !== affectedMatchId) return match;

    const matchMessages = messages
      .filter((message) => message.matchId === affectedMatchId)
      .slice()
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    const latest = matchMessages.length ? matchMessages[matchMessages.length - 1] : null;

    return {
      ...match,
      lastMessageAt: latest?.createdAt ?? match.createdAt,
    };
  });

  const next = hydrateCounts({ ...store, messages, matches });
  saveStore(next);
  return next;
}

export function markPassed(targetUserId: string) {
  const store = ensureStore();
  const me = getMe(store);
  if (me.id === targetUserId) return store;

  const passed = new Set(me.passedUserIds ?? []);
  passed.add(targetUserId);
  return updateUser({ ...me, passedUserIds: Array.from(passed) });
}

export function toggleBlock(targetUserId: string) {
  const store = ensureStore();
  const me = getMe(store);
  if (me.id === targetUserId) return store;

  const blocked = new Set(me.blockedUserIds ?? []);
  if (blocked.has(targetUserId)) blocked.delete(targetUserId);
  else blocked.add(targetUserId);

  return updateUser({ ...me, blockedUserIds: Array.from(blocked) });
}

export function createReport(params: { targetUserId: string; reason: string }) {
  const store = ensureStore();
  const me = getMe(store);
  if (me.id === params.targetUserId) return store;

  const report: KarmaReport = {
    id: uuid(),
    fromUserId: me.id,
    targetUserId: params.targetUserId,
    reason: params.reason.trim() || "Belirtilmedi",
    createdAt: nowIso(),
  };

  const next = { ...store, reports: [report, ...store.reports] };
  saveStore(next);
  return next;
}

export function createUser(params: {
  displayName: string;
  birthDate: { day: number; month: number; year: number };
  city?: string;
  intent?: KarmaUserIntent;
}) {
  const store = ensureStore();
  const createdAt = nowIso();

  const baseHandle = toHandle(params.displayName);
  let handle = baseHandle;
  let n = 2;
  while (store.users.some((u) => u.handle === handle)) {
    handle = `${baseHandle}-${n}`;
    n += 1;
  }

  const user: KarmaUser = normalizeUser({
    id: uuid(),
    handle,
    displayName: params.displayName.trim() || "Kullanıcı",
    birthDate: {
      day: params.birthDate.day,
      month: params.birthDate.month,
      year: params.birthDate.year,
    },
    bio: "",
    photos: [],
    city: params.city?.trim() || undefined,
    intent: params.intent,
    showBirthDayMonth: false,
    matchEnabled: true,
    likes: 0,
    likesBase: 0,
    blockedUserIds: [],
    passedUserIds: [],
    createdAt,
    updatedAt: createdAt,
  });

  const next: KarmaStoreV2 = hydrateCounts({
    ...store,
    users: [user, ...store.users],
  });
  saveStore(next);
  return next;
}

export function setMe(
  userId: string,
  profile?: {
    handle?: string;
    displayName?: string;
    bio?: string;
    photoUrl?: string | null;
    birthDate?: KarmaBirthDate;
  },
) {
  const store = ensureStore();
  const targetUserId = String(userId ?? "").trim();
  if (!targetUserId) return store;

  const sourceUser = getMe(store);
  const sourceUserId = sourceUser.id;
  const now = nowIso();

  const normalizedDisplayName = String(profile?.displayName ?? "").trim();
  const normalizedHandle = String(profile?.handle ?? "").trim();
  const normalizedBio = typeof profile?.bio === "string" ? profile.bio : undefined;
  const hasExplicitPhotoUrl = Boolean(profile && Object.prototype.hasOwnProperty.call(profile, "photoUrl"));
  const normalizedPhotoUrl = typeof profile?.photoUrl === "string" ? profile.photoUrl.trim() : "";
  const rawBirthDate = profile?.birthDate;
  const normalizedBirthDate =
    rawBirthDate &&
    Number.isFinite(rawBirthDate.day) &&
    Number.isFinite(rawBirthDate.month) &&
    Number.isFinite(rawBirthDate.year)
      ? {
          day: Math.trunc(rawBirthDate.day),
          month: Math.trunc(rawBirthDate.month),
          year: Math.trunc(rawBirthDate.year),
        }
      : undefined;

  const existingTarget = store.users.find((u) => u.id === targetUserId);
  const shouldMigrateSourceRelations = sourceUserId !== targetUserId;
  const replaceMeId = (value: string) =>
    shouldMigrateSourceRelations && value === sourceUserId ? targetUserId : value;

  const fallbackTargetUser: KarmaUser = normalizeUser({
    id: targetUserId,
    handle: `user-${targetUserId.slice(0, 8)}`,
    displayName: "Kullanıcı",
    birthDate: normalizedBirthDate ?? sourceUser.birthDate,
    bio: "",
    photos: [],
    matchEnabled: true,
    likes: 0,
    likesBase: 0,
    blockedUserIds: [],
    passedUserIds: [],
    createdAt: now,
    updatedAt: now,
  });

  const mergedTargetUser: KarmaUser = normalizeUser({
    ...(existingTarget ?? fallbackTargetUser),
    id: targetUserId,
    handle:
      normalizedHandle ||
      existingTarget?.handle ||
      (shouldMigrateSourceRelations ? "" : sourceUser.handle) ||
      `user-${targetUserId.slice(0, 8)}`,
    displayName:
      normalizedDisplayName ||
      existingTarget?.displayName ||
      (shouldMigrateSourceRelations ? "" : sourceUser.displayName) ||
      "Kullanıcı",
    bio: normalizedBio ?? existingTarget?.bio ?? (shouldMigrateSourceRelations ? "" : sourceUser.bio) ?? "",
    birthDate: normalizedBirthDate ?? existingTarget?.birthDate ?? sourceUser.birthDate,
    photos: normalizedPhotoUrl
      ? [
          {
            id: uuid(),
            url: normalizedPhotoUrl,
            addedAt: now,
          },
        ]
      : hasExplicitPhotoUrl
        ? []
        : existingTarget?.photos ?? [],
    blockedUserIds: uniq(
      (existingTarget?.blockedUserIds ?? (shouldMigrateSourceRelations ? sourceUser.blockedUserIds : []) ?? []).map(replaceMeId),
    ),
    passedUserIds: uniq(
      (existingTarget?.passedUserIds ?? (shouldMigrateSourceRelations ? sourceUser.passedUserIds : []) ?? []).map(replaceMeId),
    ),
    updatedAt: now,
  });

  const users = [
    mergedTargetUser,
    ...store.users
      .filter((u) => u.id !== sourceUserId && u.id !== targetUserId)
      .map((u) =>
        normalizeUser({
          ...u,
          blockedUserIds: uniq((u.blockedUserIds ?? []).map(replaceMeId)),
          passedUserIds: uniq((u.passedUserIds ?? []).map(replaceMeId)),
        }),
      ),
  ];

  const likes = store.likes
    .map((like) => ({
      ...like,
      fromUserId: replaceMeId(like.fromUserId),
      toUserId: replaceMeId(like.toUserId),
    }))
    .filter((like) => like.fromUserId !== like.toUserId)
    .filter((like) =>
      shouldMigrateSourceRelations
        ? true
        : like.fromUserId !== sourceUserId && like.toUserId !== sourceUserId,
    )
    .filter((like, index, arr) => arr.findIndex((item) => item.fromUserId === like.fromUserId && item.toUserId === like.toUserId) === index);

  const matchByPair = new Map<string, KarmaMatch>();
  const oldMatchIdToNewMatchId = new Map<string, string>();

  for (const match of store.matches) {
    const aUserId = replaceMeId(match.aUserId);
    const bUserId = replaceMeId(match.bUserId);
    if (!shouldMigrateSourceRelations && (aUserId === sourceUserId || bUserId === sourceUserId)) continue;
    if (aUserId === bUserId) continue;

    const pairKey = matchKey(aUserId, bUserId);
    const existing = matchByPair.get(pairKey);

    if (!existing) {
      const nextMatch: KarmaMatch = { ...match, aUserId, bUserId };
      matchByPair.set(pairKey, nextMatch);
      oldMatchIdToNewMatchId.set(match.id, nextMatch.id);
      continue;
    }

    oldMatchIdToNewMatchId.set(match.id, existing.id);

    const existingLast = existing.lastMessageAt ?? existing.createdAt;
    const incomingLast = match.lastMessageAt ?? match.createdAt;
    if (incomingLast > existingLast) {
      matchByPair.set(pairKey, {
        ...existing,
        lastMessageAt: match.lastMessageAt ?? existing.lastMessageAt,
      });
    }
  }

  const matches = Array.from(matchByPair.values());
  const matchById = new Map(matches.map((match) => [match.id, match]));

  const messages = store.messages
    .map((message) => {
      const nextMatchId = oldMatchIdToNewMatchId.get(message.matchId) ?? message.matchId;
      return {
        ...message,
        matchId: nextMatchId,
        fromUserId: replaceMeId(message.fromUserId),
      };
    })
    .filter((message) => {
      const match = matchById.get(message.matchId);
      if (!match) return false;
      return message.fromUserId === match.aUserId || message.fromUserId === match.bUserId;
    })
    .filter((message, index, arr) => arr.findIndex((item) => item.id === message.id) === index);

  const reports = store.reports
    .map((report) => ({
      ...report,
      fromUserId: replaceMeId(report.fromUserId),
      targetUserId: replaceMeId(report.targetUserId),
    }))
    .filter((report) => report.fromUserId !== report.targetUserId)
    .filter((report) =>
      shouldMigrateSourceRelations
        ? true
        : report.fromUserId !== sourceUserId && report.targetUserId !== sourceUserId,
    );

  const circles = (store.circles ?? []).map((circle) => ({
    ...circle,
    ownerUserId: replaceMeId(circle.ownerUserId),
    memberUserIds: uniq((circle.memberUserIds ?? []).map(replaceMeId)),
    rituals: (circle.rituals ?? []).map((ritual) => ({
      ...ritual,
      createdByUserId: replaceMeId(ritual.createdByUserId),
    })),
  }));

  const witnessRequests = (store.witnessRequests ?? []).map((request) => ({
    ...request,
    fromUserId: replaceMeId(request.fromUserId),
    verifierUserIds: uniq((request.verifierUserIds ?? []).map(replaceMeId)),
    kudosUserIds: uniq((request.kudosUserIds ?? []).map(replaceMeId)),
    reportUserIds: uniq((request.reportUserIds ?? []).map(replaceMeId)),
  }));

  const witnessActions = (store.witnessActions ?? []).map((action) => ({
    ...action,
    fromUserId: replaceMeId(action.fromUserId),
    targetUserId: replaceMeId(action.targetUserId),
  }));

  const next: KarmaStoreV2 = normalizeStore({
    ...store,
    meId: targetUserId,
    users,
    likes,
    matches,
    messages,
    reports,
    circles,
    witnessRequests,
    witnessActions,
  });

  saveStore(next);
  return next;
}
